import { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import {
  CalendarCheck,
  Edit3,
  Copy,
  Trash2,
  Calendar,
  Clock,
  ChevronRight,
  ChevronDown,
  ChevronUp,
  History,
  AlertTriangle,
  MapPin,
  Camera,
  Sparkles,
  RotateCcw,
  X,
} from 'lucide-react';
import { PageHeader } from '@/components/PageHeader';
import {
  Page,
  Main,
  Card,
  Button,
  Badge,
  Thumb,
  FitThumb,
  Modal,
  Spinner,
  LoadingState,
  ErrorState,
} from '@/components/ui';
import { WearCalendar } from './components/WearCalendar';
import { LogWearModal, uploadWearPhotos, type LogWearValues } from './components/LogWearModal';
import { useApi } from '@/hooks/useApi';
import { useAuth } from '@/lib/auth';
import api from '@/lib/api';

// The API returns errors as a JSON string body ({"error":"…"}); surface the
// message cleanly.
function errMessage(e: unknown): string {
  if (e instanceof Error) {
    try {
      const parsed = JSON.parse(e.message);
      if (parsed?.error) return parsed.error as string;
    } catch {
      /* not JSON */
    }
    return e.message;
  }
  return 'Something went wrong.';
}
import { downscaleToBase64 } from '@/lib/image';
import { STATUS_LABELS, COLOR_HEX } from '@/lib/constants';
import { formatDate, formatRelativeDate, cn } from '@/lib/utils';
import type { FitVersion, FitPiece, FitPhoto, Season } from '@/lib/types';

// GET /fits/:id wears use snake_case keys.
interface RawWear {
  id: string;
  worn_date: string;
  occasion: string | null;
  location_text: string | null;
  notes: string | null;
  version_number: number;
}

interface FitDetailResponse {
  fit: {
    id: string;
    name: string;
    seasons: Season[];
    currentVersion: number;
    imageUrl: string | null;
    piecePhotoUrls?: string[];
    photos: FitPhoto[];
    versions: FitVersion[];
    wears: RawWear[];
  };
}

export function FitDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { data, loading, error, refetch } = useApi<FitDetailResponse>(id ? `/fits/${id}` : null);

  // AI "outfit on you" render (gpt-image, ~30s). Ephemeral until saved to the fit.
  const [rendering, setRendering] = useState(false);
  const [renderError, setRenderError] = useState<string | null>(null);
  const [renderedImage, setRenderedImage] = useState<string | null>(null);
  const [savingRender, setSavingRender] = useState(false);
  const [renderSaved, setRenderSaved] = useState(false);

  const [showLogWear, setShowLogWear] = useState(false);
  const [showCopy, setShowCopy] = useState(false);
  const [showDelete, setShowDelete] = useState(false);
  const [showVersions, setShowVersions] = useState(false);
  const [deleteWearCount, setDeleteWearCount] = useState<number | null>(null);
  const [working, setWorking] = useState(false);
  const [photoBusy, setPhotoBusy] = useState(false);
  const [photoView, setPhotoView] = useState<string | null>(null);

  // Add fit-level photos (not tied to a wear). Wear photos are managed on the
  // wear's own page; both show together in this fit's gallery.
  const addFitPhotos = async (files: FileList | null) => {
    if (!files || !id) return;
    setPhotoBusy(true);
    try {
      for (const file of Array.from(files)) {
        try {
          const image = await downscaleToBase64(file);
          await api.post('/photos', { ownerType: 'fit', ownerId: id, image });
        } catch {
          /* skip a failed file */
        }
      }
      refetch();
    } finally {
      setPhotoBusy(false);
    }
  };

  const removeFitPhoto = async (photoId: string) => {
    setPhotoBusy(true);
    try {
      await api.delete(`/photos/${photoId}`);
      refetch();
    } finally {
      setPhotoBusy(false);
    }
  };

  if (loading) {
    return (
      <Page>
        <PageHeader section="Fit Details" backHref="/fits/available" backLabel="Available Fits" />
        <Main>
          <LoadingState />
        </Main>
      </Page>
    );
  }
  if (error || !data) {
    return (
      <Page>
        <PageHeader section="Fit Details" backHref="/fits/available" backLabel="Available Fits" />
        <Main>
          <ErrorState message={error ?? 'Fit not found.'} />
        </Main>
      </Page>
    );
  }

  const fit = data.fit;
  const currentVersion = fit.versions.find((v) => v.versionNumber === fit.currentVersion) ?? fit.versions[0];
  const pieces: FitPiece[] = currentVersion?.pieces ?? [];
  // Distinct colors and materials across the fit's pieces.
  const fitColors = Array.from(new Set(pieces.map((p) => p.color).filter((c): c is string => !!c)));
  const fitMaterials = Array.from(new Set(pieces.flatMap((p) => p.materials)));
  const unavailable = pieces.filter((p) => p.status !== 'in-closet' || p.isDeleted);
  const wears = [...fit.wears].sort((a, b) => new Date(b.worn_date).getTime() - new Date(a.worn_date).getTime());
  const lastWorn = wears[0]?.worn_date ?? null;
  const versionByDate: Record<string, number> = {};
  wears.forEach((w) => {
    versionByDate[w.worn_date.slice(0, 10)] = w.version_number;
  });

  const handleLogWear = async (values: LogWearValues, photos: string[]): Promise<string | null> => {
    const res = await api.post<{ id: string }>(`/fits/${fit.id}/wears`, {
      wornDate: values.wornDate,
      occasion: values.occasion || undefined,
      locationText: values.locationText || undefined,
      notes: values.notes || undefined,
    });
    if (!res?.id) return null;
    if (photos.length) await uploadWearPhotos(res.id, photos);
    refetch();
    return res.id;
  };

  const renderOutfit = async () => {
    setRendering(true);
    setRenderError(null);
    setRenderSaved(false);
    try {
      const res = await api.post<{ image: string }>('/ai/outfit-render', { fitId: fit.id });
      setRenderedImage(res.image);
    } catch (e) {
      setRenderError(errMessage(e));
    } finally {
      setRendering(false);
    }
  };

  const saveRenderToFit = async () => {
    if (!renderedImage) return;
    setSavingRender(true);
    try {
      await api.post('/photos', { ownerType: 'fit', ownerId: fit.id, image: renderedImage });
      setRenderSaved(true);
      refetch();
    } catch (e) {
      setRenderError(errMessage(e));
    } finally {
      setSavingRender(false);
    }
  };

  const handleCopy = async () => {
    setWorking(true);
    try {
      const res = await api.post<{ fit: { id: string } }>(`/fits/${fit.id}/copy`);
      if (res?.fit?.id) navigate(`/fits/${res.fit.id}`);
    } finally {
      setWorking(false);
      setShowCopy(false);
    }
  };

  const openDelete = async () => {
    setShowDelete(true);
    setDeleteWearCount(null);
    try {
      const res = await api.delete<{ wearCount: number }>(`/fits/${fit.id}?dry_run=1`);
      setDeleteWearCount(res?.wearCount ?? 0);
    } catch {
      setDeleteWearCount(0);
    }
  };

  const handleDelete = async () => {
    setWorking(true);
    try {
      await api.delete(`/fits/${fit.id}`);
      navigate('/fits/available');
    } finally {
      setWorking(false);
      setShowDelete(false);
    }
  };

  return (
    <Page>
      <PageHeader section="Fit Details" backHref="/fits/available" backLabel="Available Fits" />
      <Main className="space-y-5">
        {/* Header card */}
        <Card className="overflow-hidden">
          <div className="relative">
            <FitThumb url={fit.imageUrl} piecePhotoUrls={fit.piecePhotoUrls} alt={fit.name} className="aspect-[4/3] w-full" />
            {unavailable.length > 0 && (
              <div className="absolute top-3 right-3 px-2.5 py-1 bg-amber-100 text-amber-700 rounded-full text-xs font-medium flex items-center gap-1">
                <AlertTriangle className="w-3 h-3" />
                {unavailable.length} unavailable
              </div>
            )}
            <div className="absolute bottom-3 left-3 px-2.5 py-1 bg-card/90 backdrop-blur rounded-full text-xs font-medium text-foreground">
              v{fit.currentVersion}
            </div>
          </div>
          <div className="p-4">
            <h1 className="text-xl font-bold text-foreground">{fit.name}</h1>
            <div className="flex items-center gap-4 mt-2">
              <div className="flex items-center gap-1.5 text-sm text-muted-foreground">
                <Calendar className="w-4 h-4" />
                <span>{wears.length} wears</span>
              </div>
              <div className="flex items-center gap-1.5 text-sm text-muted-foreground">
                <Clock className="w-4 h-4" />
                <span>Last: {formatRelativeDate(lastWorn)}</span>
              </div>
            </div>
            {fit.seasons.length > 0 && (
              <div className="flex flex-wrap gap-1.5 mt-3">
                {fit.seasons.map((s) => (
                  <span key={s} className="px-2 py-0.5 bg-secondary rounded-full text-xs font-medium text-muted-foreground capitalize">
                    {s}
                  </span>
                ))}
              </div>
            )}
          </div>
        </Card>

        {/* Actions */}
        <div className="grid grid-cols-2 gap-3">
          <button
            onClick={() => setShowLogWear(true)}
            className="flex flex-col items-center gap-2 bg-primary text-primary-foreground rounded-xl p-3 hover:opacity-90 transition-all"
          >
            <CalendarCheck className="w-5 h-5" />
            <span className="text-xs font-medium">Log Wear</span>
          </button>
          <Link
            to={`/fits/builder?edit=${fit.id}`}
            className="flex flex-col items-center gap-2 bg-card border border-border rounded-xl p-3 hover:border-primary/30 transition-all text-foreground"
          >
            <Edit3 className="w-5 h-5" />
            <span className="text-xs font-medium">Edit Fit</span>
          </Link>
          <button
            onClick={() => setShowCopy(true)}
            className="flex flex-col items-center gap-2 bg-card border border-border rounded-xl p-3 hover:border-primary/30 transition-all text-foreground"
          >
            <Copy className="w-5 h-5" />
            <span className="text-xs font-medium">Copy Fit</span>
          </button>
          <button
            onClick={openDelete}
            className="flex flex-col items-center gap-2 bg-card border border-border rounded-xl p-3 hover:border-destructive/50 hover:text-destructive transition-all text-foreground"
          >
            <Trash2 className="w-5 h-5" />
            <span className="text-xs font-medium">Delete</span>
          </button>
        </div>

        {/* See this outfit on you (AI try-on) */}
        <Card className="p-4 space-y-3">
          <div className="flex items-center gap-2">
            <Sparkles className="w-4 h-4 text-primary" />
            <h2 className="text-sm font-semibold text-foreground">See this outfit on you</h2>
          </div>

          {renderError && <p className="text-xs text-destructive">{renderError}</p>}

          {renderedImage ? (
            <div className="space-y-3">
              <button type="button" onClick={() => setPhotoView(renderedImage)} className="block w-full">
                <img
                  src={renderedImage}
                  alt={`${fit.name} on you`}
                  className="w-full rounded-xl bg-secondary"
                />
              </button>
              <div className="flex gap-2">
                <Button variant="secondary" fullWidth onClick={renderOutfit} disabled={rendering || savingRender}>
                  {rendering ? <Spinner className="w-4 h-4" /> : <RotateCcw className="w-4 h-4" />}
                  {rendering ? 'Rendering…' : 'Regenerate'}
                </Button>
                <Button fullWidth onClick={saveRenderToFit} disabled={savingRender || rendering || renderSaved}>
                  {savingRender ? 'Saving…' : renderSaved ? 'Saved' : 'Save to fit'}
                </Button>
              </div>
            </div>
          ) : !user?.avatarUrl ? (
            <div className="space-y-2">
              <p className="text-xs text-muted-foreground">
                Add a photo of yourself and we'll render this outfit on you.
              </p>
              <Button variant="secondary" fullWidth onClick={() => navigate('/profile')}>
                <Camera className="w-4 h-4" />
                Add your photo
              </Button>
            </div>
          ) : (
            <div className="space-y-2">
              <p className="text-xs text-muted-foreground">
                Generate an editorial photo of you wearing this fit. Takes around 30 seconds.
              </p>
              <Button fullWidth onClick={renderOutfit} disabled={rendering}>
                {rendering ? <Spinner className="w-4 h-4" /> : <Sparkles className="w-4 h-4" />}
                {rendering ? 'Rendering…' : 'See it on me'}
              </Button>
            </div>
          )}
        </Card>

        {/* Items */}
        <Card className="overflow-hidden">
          <div className="p-4 border-b border-border">
            <h2 className="text-sm font-semibold text-foreground">Items in Fit (v{fit.currentVersion})</h2>
            <p className="text-xs text-muted-foreground mt-0.5">{pieces.length} pieces</p>
            {(fitColors.length > 0 || fitMaterials.length > 0) && (
              <div className="mt-2 flex flex-wrap gap-1.5">
                {fitColors.map((c) => (
                  <span
                    key={`c-${c}`}
                    className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-secondary text-[10px] font-medium text-foreground"
                  >
                    <span
                      className="w-2.5 h-2.5 rounded-full border border-border"
                      style={{ backgroundColor: COLOR_HEX[c] ?? '#ccc' }}
                    />
                    {c}
                  </span>
                ))}
                {fitMaterials.map((m) => (
                  <span
                    key={`m-${m}`}
                    className="px-2 py-0.5 rounded-full bg-secondary text-[10px] font-medium text-muted-foreground"
                  >
                    {m}
                  </span>
                ))}
              </div>
            )}
          </div>
          <div className="divide-y divide-border">
            {pieces.map((piece) => {
              const flagged = piece.status !== 'in-closet' || piece.isDeleted;
              return (
                <div key={piece.id} className="p-4">
                  <div className="flex gap-3">
                    <Thumb url={piece.primaryPhotoUrl} alt={piece.name} className="w-14 h-14 rounded-lg flex-shrink-0" />
                    <div className="flex-1 min-w-0">
                      <Link
                        to={`/wardrobe/item/${piece.itemId}`}
                        className="text-sm font-medium text-foreground leading-tight hover:text-primary transition-colors"
                      >
                        {piece.name}
                      </Link>
                      {piece.brand && <p className="text-xs text-muted-foreground mt-0.5">{piece.brand}</p>}
                      {(piece.color || piece.materials.length > 0) && (
                        <p className="text-xs text-muted-foreground mt-0.5 flex items-center gap-1.5 flex-wrap">
                          {piece.color && (
                            <span className="inline-flex items-center gap-1">
                              <span
                                className="w-2.5 h-2.5 rounded-full border border-border"
                                style={{ backgroundColor: COLOR_HEX[piece.color] ?? '#ccc' }}
                              />
                              {piece.color}
                            </span>
                          )}
                          {piece.color && piece.materials.length > 0 && <span>·</span>}
                          {piece.materials.length > 0 && <span>{piece.materials.join(', ')}</span>}
                        </p>
                      )}
                      <div className="flex items-center gap-2 mt-1.5">
                        <Badge tone={flagged ? 'warning' : 'success'}>
                          {piece.isDeleted ? 'Deleted' : STATUS_LABELS[piece.status] ?? piece.status}
                        </Badge>
                        <span className="text-[10px] text-muted-foreground">{piece.wearCount} wears</span>
                      </div>
                    </div>
                  </div>
                  {flagged && (
                    <div className="mt-3 flex items-center justify-between gap-2 bg-amber-50 border border-amber-200 rounded-lg p-2.5">
                      <p className="text-xs text-amber-700 flex items-center gap-1.5">
                        <AlertTriangle className="w-3.5 h-3.5 flex-shrink-0" />
                        This piece is not available.
                      </p>
                      <Link
                        to={`/fits/builder?edit=${fit.id}`}
                        className="text-xs font-medium text-primary hover:underline flex-shrink-0"
                      >
                        Edit
                      </Link>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </Card>

        {/* Photos — fit-level photos + every photo across this fit's wears. */}
        <Card className="overflow-hidden">
          <div className="p-4 border-b border-border flex items-center justify-between">
            <h2 className="text-sm font-semibold text-foreground">Photos ({fit.photos.length})</h2>
            <label
              className={cn(
                'inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg bg-secondary text-xs font-medium text-foreground hover:bg-muted transition-colors cursor-pointer',
                photoBusy && 'opacity-50 pointer-events-none',
              )}
            >
              <Camera className="w-3.5 h-3.5" />
              {photoBusy ? 'Uploading…' : 'Add Photo'}
              <input
                type="file"
                accept="image/*"
                multiple
                className="hidden"
                onChange={(e) => addFitPhotos(e.target.files)}
              />
            </label>
          </div>
          <div className="p-4">
            {fit.photos.length === 0 ? (
              <p className="text-sm text-muted-foreground text-center py-2">
                No photos yet. Add photos of this fit here, or they'll appear when you add them to a wear.
              </p>
            ) : (
              <div className="grid grid-cols-3 gap-2">
                {fit.photos.map((photo) => (
                  <div key={photo.id} className="relative aspect-square">
                    <button
                      onClick={() => setPhotoView(photo.url)}
                      className="w-full h-full rounded-xl overflow-hidden bg-secondary"
                    >
                      <img src={photo.url} alt="" className="w-full h-full object-cover" />
                    </button>
                    <button
                      type="button"
                      onClick={() => removeFitPhoto(photo.id)}
                      disabled={photoBusy}
                      className="absolute -top-2 -right-2 w-6 h-6 rounded-full bg-card border border-border flex items-center justify-center hover:bg-destructive hover:text-white transition-colors"
                      aria-label="Delete photo"
                    >
                      <X className="w-3.5 h-3.5" />
                    </button>
                    {photo.source === 'wear' && (
                      <span className="absolute bottom-1 left-1 right-1 px-1 py-0.5 bg-foreground/60 text-white text-[8px] font-medium rounded text-center truncate">
                        {photo.wornDate ? formatDate(photo.wornDate) : 'Wear'}
                      </span>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </Card>

        {/* Wear calendar */}
        <Card className="p-4">
          <WearCalendar
            wornDates={wears.map((w) => w.worn_date)}
            currentVersion={fit.currentVersion}
            versionByDate={versionByDate}
          />
        </Card>

        {/* Recent wears */}
        {wears.length > 0 && (
          <Card className="overflow-hidden">
            <div className="p-4 border-b border-border">
              <h2 className="text-sm font-semibold text-foreground">Recent Wears</h2>
            </div>
            <div className="divide-y divide-border">
              {wears.slice(0, 5).map((w) => (
                <Link
                  key={w.id}
                  to={`/fits/${fit.id}/wear/${w.id}`}
                  className="p-3 flex items-center gap-3 hover:bg-secondary/50 transition-colors"
                >
                  <div className="w-10 h-10 bg-secondary rounded-lg flex items-center justify-center flex-shrink-0">
                    <Calendar className="w-4 h-4 text-muted-foreground" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-foreground">{formatDate(w.worn_date)}</p>
                    {w.location_text && (
                      <p className="text-xs text-muted-foreground flex items-center gap-1">
                        <MapPin className="w-3 h-3" />
                        {w.location_text}
                      </p>
                    )}
                  </div>
                  <span className="px-2 py-0.5 bg-secondary rounded text-[10px] font-medium text-muted-foreground">
                    v{w.version_number}
                  </span>
                  <ChevronRight className="w-4 h-4 text-muted-foreground" />
                </Link>
              ))}
            </div>
          </Card>
        )}

        {/* Version history */}
        <Card className="overflow-hidden">
          <button
            onClick={() => setShowVersions((v) => !v)}
            className="w-full p-4 flex items-center justify-between hover:bg-secondary/50 transition-colors"
          >
            <div className="flex items-center gap-3">
              <History className="w-5 h-5 text-muted-foreground" />
              <div className="text-left">
                <h2 className="text-sm font-semibold text-foreground">Version History</h2>
                <p className="text-xs text-muted-foreground">{fit.versions.length} versions</p>
              </div>
            </div>
            {showVersions ? (
              <ChevronUp className="w-5 h-5 text-muted-foreground" />
            ) : (
              <ChevronDown className="w-5 h-5 text-muted-foreground" />
            )}
          </button>
          {showVersions && (
            <div className="border-t border-border divide-y divide-border">
              {[...fit.versions].reverse().map((v) => (
                <Link
                  key={v.id}
                  to={`/fits/${fit.id}/version/${v.versionNumber}`}
                  className="block p-4 hover:bg-secondary/50 transition-colors"
                >
                  <div className="flex items-center justify-between mb-1">
                    <div className="flex items-center gap-2">
                      <span
                        className={`px-2 py-0.5 rounded text-xs font-medium ${
                          v.versionNumber === fit.currentVersion
                            ? 'bg-primary text-primary-foreground'
                            : 'bg-secondary text-muted-foreground'
                        }`}
                      >
                        v{v.versionNumber}
                      </span>
                      {v.versionNumber === fit.currentVersion && (
                        <span className="text-xs text-green-600 font-medium">Current</span>
                      )}
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-muted-foreground">{formatDate(v.createdAt)}</span>
                      <ChevronRight className="w-4 h-4 text-muted-foreground" />
                    </div>
                  </div>
                  {v.note && <p className="text-xs text-muted-foreground italic mb-1">{v.note}</p>}
                  <p className="text-xs text-muted-foreground">{v.pieces.length} pieces</p>
                </Link>
              ))}
            </div>
          )}
        </Card>
      </Main>

      {showLogWear && (
        <LogWearModal
          isOpen
          onClose={() => setShowLogWear(false)}
          fitName={fit.name}
          pieceCount={pieces.length}
          onSubmit={handleLogWear}
        />
      )}

      {/* Copy confirm */}
      <Modal
        isOpen={showCopy}
        onClose={() => setShowCopy(false)}
        title="Copy Fit"
        footer={
          <div className="flex gap-3">
            <Button variant="outline" fullWidth onClick={() => setShowCopy(false)} disabled={working}>
              Cancel
            </Button>
            <Button fullWidth onClick={handleCopy} disabled={working}>
              <Copy className="w-4 h-4" />
              Copy Fit
            </Button>
          </div>
        }
      >
        <p className="text-sm text-muted-foreground">
          Create a copy of "{fit.name}"? The new fit will start at version 1.
        </p>
      </Modal>

      {/* Delete confirm */}
      <Modal
        isOpen={showDelete}
        onClose={() => setShowDelete(false)}
        title="Delete Fit"
        footer={
          <div className="flex gap-3">
            <Button variant="outline" fullWidth onClick={() => setShowDelete(false)} disabled={working}>
              Cancel
            </Button>
            <Button variant="destructive" fullWidth onClick={handleDelete} disabled={working}>
              <Trash2 className="w-4 h-4" />
              Delete
            </Button>
          </div>
        }
      >
        <div className="flex items-start gap-3 bg-destructive/10 rounded-xl p-3">
          <AlertTriangle className="w-5 h-5 text-destructive flex-shrink-0 mt-0.5" />
          <p className="text-sm text-foreground">
            This will permanently delete "{fit.name}"
            {deleteWearCount === null
              ? '…'
              : deleteWearCount > 0
              ? ` and its ${deleteWearCount} wear record${deleteWearCount > 1 ? 's' : ''}.`
              : '.'}{' '}
            This cannot be undone.
          </p>
        </div>
      </Modal>

      {/* Photo lightbox */}
      {photoView && (
        <div
          className="fixed inset-0 z-50 bg-black/90 flex items-center justify-center p-4"
          onClick={() => setPhotoView(null)}
        >
          <button
            className="absolute top-4 right-4 p-2 text-white/80 hover:text-white"
            onClick={() => setPhotoView(null)}
          >
            <X className="w-6 h-6" />
          </button>
          <img src={photoView} alt="" className="max-w-full max-h-full object-contain" />
        </div>
      )}
    </Page>
  );
}
